import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiFileText, FiDownload, FiX } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { MODAL_Z } from './ModalPortal';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const FIELD_ALIASES = {
  nameOfMedicine: ['Name Of Medicine', 'Name', 'Medicine', 'Medicine Name', 'Item', 'Item Name', 'Product', 'Product Name'],
  company: ['Name of Company', 'Company', 'Company Name', 'Brand', 'Manufacturer'],
  potencyOrPower: ['Potency Or Power', 'Potency', 'Power', 'Strength'],
  quantity: ['Quantity', 'Qty', 'Stock', 'Current Qty', 'Current Quantity', 'Opening Stock'],
  typeOfMedicine: ['Type Of Medicine', 'Type', 'Form', 'Medicine Type', 'Category'],
  distributorName: ['Distributor Name', 'Distributor', 'Supplier', 'Vendor'],
  package: ['Packaging', 'Package', 'Package ml', 'Pack'],
  packagingSize: ['Packaging Size', 'Pack Size', 'Packing', 'Packing Size'],
  expiryDate: ['Expiry Date', 'Expiry', 'Exp', 'Exp Date', 'Expire Date'],
  mafDate: ['Mfg Date', 'Maf Date', 'Mf Date', 'Manufacturing Date', 'Manufacture Date'],
  minQuantity: ['Min Quantity', 'Min Qty', 'Min', 'Minimum Quantity', 'Reorder Level'],
  maxQuantity: ['Max Quantity', 'Max Qty', 'Max', 'Maximum Quantity'],
  mrp: ['MRP', 'Price', 'Rate', 'MRP Price', 'Unit Price'],
  discount: ['Discount', 'Disc', 'Discount Percent', 'Discount %'],
  hsnCode: ['HSN Code', 'HSN', 'HSN No'],
  gst: ['GST', 'GST %', 'GST Percent', 'GST Rate', 'Tax', 'Tax %'],
  orderId: ['Order ID', 'Order', 'Order No'],
  invoiceNo: ['Invoice No', 'Invoice', 'Invoice Number', 'Bill No'],
};

const normalizeHeader = (header) => String(header ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const HEADER_LOOKUP = Object.entries(FIELD_ALIASES).reduce((lookup, [field, aliases]) => {
  aliases.forEach((alias) => {
    lookup[normalizeHeader(alias)] = field;
  });
  return lookup;
}, {});

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadMedicineModal = ({ isOpen, onClose, onUpload, onDownloadTemplate }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Start from a clean slate every time the modal is reopened
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setIsUploading(false);
    }
  }, [isOpen]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file || null);
  };

  const downloadTemplate = () => {
    const templateFilePath = process.env.PUBLIC_URL + '/inventory/template.xlsx';
    const link = document.createElement('a');
    link.href = templateFilePath;
    link.download = 'template.xlsx';
    link.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const workbook = await readExcel(selectedFile);
      const jsonData = excelToJson(workbook);

      const reversedData = {};
      const unmatchedHeaders = new Set();

      for (const key in jsonData) {
        if (Object.hasOwnProperty.call(jsonData, key)) {
          const transformedArray = jsonData[key]?.map((data) => {
            const transformedData = {};
            for (const innerKey in data) {
              if (Object.hasOwnProperty.call(data, innerKey)) {
                const field = HEADER_LOOKUP[normalizeHeader(innerKey)];
                if (field) transformedData[field] = data[innerKey];
                else unmatchedHeaders.add(String(innerKey).trim());
              }
            }
            return transformedData;
          });

          reversedData[key] = transformedArray;
        }
      }

      const allDataArray = [].concat(...Object.values(reversedData));
      if (!allDataArray.length) {
        throw new Error('That sheet has no rows to import');
      }

      if (!allDataArray.some((row) => row.nameOfMedicine)) {
        throw new Error(
          `No medicine-name column found. Columns read: ${[...unmatchedHeaders].join(', ') || 'none'}`
        );
      }

      if (unmatchedHeaders.size) {
        toast.warn(`Ignored unrecognised column${unmatchedHeaders.size === 1 ? '' : 's'}: ${[...unmatchedHeaders].join(', ')}`);
        console.warn('Unrecognised sheet columns', [...unmatchedHeaders]);
      }
      await onUpload(allDataArray);
      setSelectedFile(null);
      if (onClose) onClose();
    } catch (error) {
      toast.error(error?.message || error?.response?.data || 'Something went wrong');
      console.error('Error processing file:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const readExcel = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > MAX_FILE_SIZE) {
        reject(new Error(`File is ${formatSize(file.size)} — files larger than ${formatSize(MAX_FILE_SIZE)} are not allowed`));
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
        resolve(workbook);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsBinaryString(file);
    });
  };

  const excelToJson = (workbook) => {
    const result = {};
    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      const jsonSheet = XLSX.utils.sheet_to_json(sheet);

      const filteredSheet = jsonSheet
        ?.map((row) => {
          const { __rowNum__, ...filteredRow } = row;
          return filteredRow;
        })
        ?.filter((row) => Object.values(row).some((value) => String(value ?? '').trim() !== ''));

      result[sheetName] = filteredSheet;
    });
    return result;
  };

  if (!isOpen) return null;

  const isTooLarge = selectedFile && selectedFile.size > MAX_FILE_SIZE;

  return (
    <div className={`fixed inset-0 ${MODAL_Z} overflow-y-auto`}>
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="relative flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white text-left shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">Upload Medicines</h3>
              <p className="mt-0.5 text-xs text-slate-500">Import your stock in bulk from an Excel sheet.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <label
              htmlFor="fileInput"
              className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center transition hover:border-sky-400 hover:bg-sky-50"
            >
              <FiUploadCloud className="h-8 w-8 text-slate-400" />
              <span className="mt-3 text-sm font-medium text-slate-700">
                {selectedFile ? 'Choose a different file' : 'Click to select an Excel file'}
              </span>
              <span className="mt-1 text-xs text-slate-500">.xlsx or .xls — up to {formatSize(MAX_FILE_SIZE)}</span>
              <input
                type="file"
                id="fileInput"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                className="sr-only"
              />
            </label>

            {selectedFile && (
              <div
                className={`mt-3 flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                  isTooLarge ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white'
                }`}
              >
                <FiFileText className={`h-5 w-5 flex-shrink-0 ${isTooLarge ? 'text-rose-500' : 'text-sky-600'}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{selectedFile.name}</p>
                  <p className={`text-xs ${isTooLarge ? 'text-rose-600' : 'text-slate-500'}`}>
                    {formatSize(selectedFile.size)}
                    {isTooLarge ? ` — too large, max ${formatSize(MAX_FILE_SIZE)}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  aria-label="Remove file"
                  className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
              <p className="text-xs text-slate-600">Not sure about the format? Start from our template.</p>
              <button
                type="button"
                onClick={onDownloadTemplate || downloadTemplate}
                className="inline-flex flex-shrink-0 items-center gap-1.5 text-xs font-semibold text-sky-700 transition hover:text-sky-800"
              >
                <FiDownload className="h-4 w-4" />
                Download
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || isTooLarge || isUploading}
              className="rounded-xl bg-sky-600 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-sky-600/30 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadMedicineModal;
